import { useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import BaseCard from '../common/layout/BaseCard';
import { Modal } from '../common/ui/Modal';
import { Button } from '../common/ui/Button';
import { captureAppException, createSupabaseError } from '../../utils/errorCaptureUtils';

// Badge interfaces based on the SQL schema
interface Badge {
  badge_id: string;
  name: string;
  description: string;
  category: 'membership' | 'activity' | 'community' | 'special' | 'milestones' | 'projects' | 'events';
  rarity: 'Common' | 'Uncommon' | 'Rare' | 'Epic' | 'Legendary';
  image_url: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  conditions?: string;
}

interface UserBadge {
  user_badge_id: string;
  user_id: string;
  badge_id: string;
  created_at: string;
  visibility: 'public' | 'hidden';
  badge?: Badge; // Joined badge data
}

// Modal component to display badge details
interface BadgeModalProps {
  userBadge: UserBadge;
  isOpen: boolean;
  onClose: () => void;
}

// Rarity Slider component
interface RaritySliderProps {
  rarity: Badge['rarity'];
}

const RaritySlider: React.FC<RaritySliderProps> = ({ rarity }) => {
  // Map rarity to a position on the slider (0-100%)
  const getPosition = () => {
    switch (rarity) {
      case 'Common': return 0;
      case 'Uncommon': return 25;
      case 'Rare': return 50;
      case 'Epic': return 75;
      case 'Legendary': return 100;
      default: return 0;
    }
  };

  return (
    <div className="flex flex-col items-center mt-2">
      <div className="w-24 h-2 rounded-full bg-gradient-to-r from-cyan-500 via-purple-500 to-orange-500 relative">
        <div 
          className="absolute top-0 w-3 h-3 bg-white border-2 border-gray-700 rounded-full -mt-0.5"
          style={{ left: `${getPosition()}%`, transform: 'translateX(-50%)' }}
        />
      </div>
      <span className="text-sm text-gray-500 mt-1">{rarity}</span>
    </div>
  );
};

const BadgeModal: React.FC<BadgeModalProps> = ({ userBadge, isOpen, onClose }) => {
  if (!isOpen || !userBadge.badge) return null;

  const badge = userBadge.badge;
  const awarded = new Date(userBadge.created_at);
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="relative flex flex-col h-full">
        {/* Close (X) button for desktop only */}
        <div className="absolute top-0 right-0 hidden sm:block">
          <button
            type="button"
            className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={onClose}
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex flex-col items-center px-4 py-6 flex-grow justify-center min-h-[70vh] sm:min-h-0">
          {/* Title */}
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            {badge.name}
          </h3>
          
          {/* Description */}
          <p className="text-gray-600 text-center mb-6">
            {badge.description}
          </p>
          
          {/* Larger badge image with shimmer effect */}
          <div className="mb-3 relative overflow-hidden">
            <div 
              className="absolute inset-0 w-[200%] bg-gradient-to-r from-transparent via-white to-transparent opacity-70 z-10
                animate-[shimmer_1s_ease-out_forwards]"
              style={{ 
                animationDelay: '0.1s',
                animationIterationCount: 1,
                transformOrigin: 'center',
                top: '-100%',
                height: '300%'
              }}
            />
            <img 
              src={badge.image_url} 
              alt={badge.name} 
              className="w-48 h-48 object-contain relative z-0" 
            />
          </div>

          {/* Rarity slider */}
          <RaritySlider rarity={badge.rarity} />
          
          {/* Awarded date */}
          <div className="text-sm text-gray-500 mt-4">
            Unlocked on {awarded.toLocaleDateString()}
          </div>
        </div>
        
        {/* Close button for mobile only, positioned at bottom */}
        <div className="border-t border-gray-200 p-4 sm:hidden">
          <Button 
            type="button"
            variant="outline"
            className="w-full"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Main component to display user badges
interface UserBadgesProps {
  userId: string;
}

const UserBadges: React.FC<UserBadgesProps> = ({ userId }) => {
  const [userBadges, setUserBadges] = useState<UserBadge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBadge, setSelectedBadge] = useState<UserBadge | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchUserBadges = async () => {
      try {
        setLoading(true);
        
        // Fetch user badges with badge details
        const { data, error } = await supabase
          .from('user_badges')
          .select(`
            user_badge_id,
            user_id,
            badge_id,
            created_at,
            visibility,
            badges (
              badge_id,
              name,
              description,
              category,
              rarity,
              image_url,
              created_at,
              updated_at,
              is_active,
              conditions
            )
          `)
          .eq('user_id', userId)
          .eq('visibility', 'public');
        
        if (error) throw createSupabaseError(error, 'fetch_user_badges');
        
        // Transform data to include badge info
        const transformedData = data.map((userBadge: any) => ({
          ...userBadge,
          badge: userBadge.badges
        }));
        
        setUserBadges(transformedData);
      } catch (err) {
        captureAppException(err, {
          tags: {
            context: 'user_badges',
            user_id: userId,
            action: 'fetch_user_badges'
          }
        });
        console.error('Error fetching user badges:', err);
        setError('Failed to load badges');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserBadges();
  }, [userId]);

  const handleOpenModal = (userBadge: UserBadge) => {
    setSelectedBadge(userBadge);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (loading) {
    return (
      <BaseCard>
        <h2 className="text-lg font-bold text-gray-900 mb-4">Badges</h2>
        <div className="flex items-center justify-center min-h-[100px]">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
      </BaseCard>
    );
  }

  if (error) {
    return (
      <BaseCard>
        <h2 className="text-lg font-bold text-gray-900 mb-4">Badges</h2>
        <p className="text-red-500">{error}</p>
      </BaseCard>
    );
  }

  return (
    <BaseCard>
      <h2 className="text-lg font-bold text-gray-900 mb-4">Badges</h2>
      
      {userBadges.length === 0 ? (
        <p className="text-gray-500 italic">No badges yet</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
          {userBadges.map((userBadge) => (
            <div 
              key={userBadge.user_badge_id}
              className="flex justify-center items-center"
            >
              <div 
                className="w-16 h-16 flex items-center justify-center cursor-pointer transition-all duration-200 hover:bg-gray-100 rounded-lg p-1"
                onClick={() => handleOpenModal(userBadge)}
              >
                <img 
                  src={userBadge.badge?.image_url} 
                  alt={userBadge.badge?.name} 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          ))}
        </div>
      )}
      
      {selectedBadge && (
        <BadgeModal 
          userBadge={selectedBadge} 
          isOpen={isModalOpen} 
          onClose={handleCloseModal} 
        />
      )}
    </BaseCard>
  );
};

export default UserBadges; 